// SYSTEM_ROUTE: /projects
import { useState } from 'react';
import type { Project, ProjectStatus } from '@life-os/app-core';
import { PROJECT_STATUSES, isProjectStalled, summarizeProjects } from '@life-os/app-core';
import { Button, Chip, EmptyState, Pane, Stack } from '@life-os/ui';
import type { ChipTone } from '@life-os/ui';
import { useServices, useStore } from '../app/store.js';
import { useAsync } from '../lib/useAsync.js';
import { ScreenHeader } from '../app/ScreenHeader.js';

const STATUS_TONE: Record<ProjectStatus, ChipTone> = {
  idea: 'neutral',
  active: 'primary',
  paused: 'muted',
  shipped: 'primary',
  archived: 'muted',
};

// Display order: things in motion first, then at rest.
const STATUS_ORDER: ProjectStatus[] = ['active', 'idea', 'paused', 'shipped', 'archived'];

const inputStyle = {
  background: 'var(--lc-surface-container)',
  color: 'var(--lc-on-surface)',
  border: '1px solid var(--lc-outline-variant)',
  borderRadius: 'var(--lc-radius-full)',
  padding: '6px 12px',
  font: 'inherit',
  fontSize: 'var(--lc-text-sm)',
} as const;

function ProjectCard({ project, onChange }: { project: Project; onChange: () => void }) {
  const services = useServices();
  const [nextAction, setNextAction] = useState(project.nextAction);
  const stalled = isProjectStalled(project);

  const saveNext = async () => {
    if (nextAction === project.nextAction) return;
    await services.projects.update(project.id, { nextAction });
    onChange();
  };

  return (
    <div className="lc-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lc-stack-sm)', flexWrap: 'wrap' }}>
        <strong style={{ flex: 1 }}>{project.title}</strong>
        {stalled ? <Chip tone="warn">Needs a decision</Chip> : null}
        <Chip tone={STATUS_TONE[project.status]}>{project.status}</Chip>
        <select
          value={project.status}
          onChange={(e) => void services.projects.setStatus(project.id, e.target.value as ProjectStatus).then(onChange)}
          aria-label="Project status"
          style={inputStyle}
        >
          {PROJECT_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>
      {project.summary ? (
        <div style={{ color: 'var(--lc-on-surface-variant)', fontSize: 'var(--lc-text-sm)' }}>{project.summary}</div>
      ) : null}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lc-stack-sm)' }}>
        <span style={{ color: 'var(--lc-on-surface-variant)', fontSize: 'var(--lc-text-sm)' }}>Next</span>
        <input
          className="lc-cmdbar"
          style={{ flex: 1 }}
          value={nextAction}
          placeholder="The single next move…"
          onChange={(e) => setNextAction(e.target.value)}
          onBlur={() => void saveNext()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void saveNext();
          }}
          aria-label="Next action"
        />
      </div>
      {project.definitionOfDone ? (
        <div style={{ color: 'var(--lc-on-surface-variant)', fontSize: 'var(--lc-text-sm)' }}>
          Done when: {project.definitionOfDone}
        </div>
      ) : null}
    </div>
  );
}

export function ProjectsScreen() {
  const services = useServices();
  const { revision, refresh } = useStore();
  const [title, setTitle] = useState('');
  const [dod, setDod] = useState('');

  const projects = useAsync<readonly Project[]>(() => services.projects.list(), [revision]);

  const create = async () => {
    if (title.trim().length === 0) return;
    await services.projects.create({ title, definitionOfDone: dod });
    setTitle('');
    setDod('');
    refresh();
  };

  const items = projects.value ?? [];
  const summary = summarizeProjects(items);

  return (
    <>
      <ScreenHeader title={<>Projects</>} subtitle={<>One canonical home per body of work. Each carries a single next action and a clear definition
            of done — so projects move from idea to proof, not from list to list.</>} />

      <Pane title="Start a project" hint="A title and what 'done' means is enough to begin.">
        <div style={{ display: 'flex', gap: 'var(--lc-stack-sm)', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            className="lc-cmdbar"
            style={{ flex: '1 1 200px' }}
            value={title}
            placeholder="e.g. Ship the portfolio site"
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void create();
            }}
            aria-label="Project title"
          />
          <input
            className="lc-cmdbar"
            style={{ flex: '1 1 240px' }}
            value={dod}
            placeholder="Done when… (the proof)"
            onChange={(e) => setDod(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void create();
            }}
            aria-label="Definition of done"
          />
          <Button variant="primary" onClick={() => void create()}>
            Add project
          </Button>
        </div>
      </Pane>

      <Pane
        title="Portfolio"
        hint={`${summary.active} active · ${summary.shipped} shipped${summary.stalled > 0 ? ` · ${summary.stalled} need a decision` : ''}`}
      >
        {items.length === 0 ? (
          <EmptyState title="No projects yet">
            Turn an intention into one canonical project. Tasks, targets, and notes link to it instead of
            scattering.
          </EmptyState>
        ) : (
          <Stack>
            {STATUS_ORDER.map((status) => {
              const inStatus = items.filter((p) => p.status === status);
              if (inStatus.length === 0) return null;
              return (
                <div key={status}>
                  <div style={{ margin: '4px 0 8px', textTransform: 'capitalize', fontWeight: 600 }}>{status}</div>
                  <Stack>
                    {inStatus.map((p) => (
                      <ProjectCard key={String(p.id)} project={p} onChange={refresh} />
                    ))}
                  </Stack>
                </div>
              );
            })}
          </Stack>
        )}
      </Pane>
    </>
  );
}
